import React from "react";
import { ButtonColor, ButtonVariant, Size, Width } from "../utils/types";
import { mapValue } from "../utils/helpers";
import { widthMapping } from "../utils/mappings";

export const buttonSizeMapping = {
  xs: "w-20 h-8 py-7 text-xs",
  s: "w-24 h-8.5 py-[9px] leading-16px text-sm",
  m: "w-26 h-9.5 py-[9px] text-sm",
  l: "w-28 h-10.5 py-[9px] text-base",
  xl: "w-32 h-12.5 py-13 text-base",
};
export const buttonColorMapping = {
  primary: "bg-primary-600 hover:bg-primary-700 border border-transparent text-white",
  secondary: "bg-primary-100 hover:bg-primary-200 border border-transparent text-primary-700",
  tertiary: "bg-white hover:bg-neutral-100 border border-gray-300 text-neutral-800",
  warning:
    "bg-red-500 hover:bg-red-600 text-white focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:shadow-none",
  link: "bg-transparent text-primary-600 hover:bg-primary-100 focus:ring-2 focus:ring-primary-500",
  selected: "bg-primary-600 text-white",
  // note that `success` variant does not have hover or focus styles since "success" buttons should not be clickable/interactive
  success: "bg-green-100 text-green-600 font-medium",
  info: "bg-blue-600 hover:bg-blue-700 border border-transparent text-white",
};

export const buttonVariantMapping = {
  pill: "rounded-md",
  round: "rounded-full",
  leftRounded: "rounded-l-md",
  rightRounded: "rounded-r-md",
  noRound: "rounded-none",
};

interface ButtonProps {
  children: React.ReactNode;
  color: ButtonColor;
  width?: Width;
  onClick: (() => void) | React.MouseEventHandler<HTMLButtonElement>;
  size?: Size;
  variant?: ButtonVariant;
  isCircular?: boolean;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  customSizeClassname?: string; // e.g. "h-[30px] text-[12px]", if this is set then will ignore the `size` prop
}

function Button({
  children,
  color,
  width = "auto",
  onClick,
  size = "m",
  variant = "pill",
  isCircular = false,
  disabled = false,
  type = "button",
  customSizeClassname,
}: ButtonProps) {
  // Define padding based on the isCircular flag
  const pxPadding = isCircular
    ? {
        xs: "px-1.5",
        s: "px-2",
        m: "px-2.5",
        l: "px-2.5",
        xl: "px-3",
      }[size]
    : {
        xs: "px-2.5",
        s: "px-3",
        m: "px-4",
        l: "px-4",
        xl: "px-6",
      }[size]; // Adjust these values if needed

  let sizeClassnames =
    customSizeClassname && customSizeClassname.length > 0
      ? customSizeClassname
      : buttonSizeMapping[size];

  const buttonClassName = `${buttonVariantMapping[variant]} ${sizeClassnames} ${mapValue<
    Width,
    string
  >(widthMapping, width)} ${pxPadding} inline-flex justify-center items-center ${
    buttonColorMapping[color]
  } font-medium   focus:outline-none
  focus:ring-2 focus:ring-offset-2
  focus:ring-primary-600
  focus:shadow-focusPrimary
  ${disabled ? "opacity-50 cursor-not-allowed" : ""}
  ${color !== "link" ? "shadow-sm" : ""}
  `;

  return (
    <button type={type} className={buttonClassName} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export default Button;
